import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { get, head, reduce, tail } from 'lodash-es';
import type { RasterLayerSpecification, SourceSpecification } from 'mapbox-gl';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class WeatherLayerService {
    sourceIdCloudCover = 'cloudCover';
    sourceIdRadar = 'radar';
    sourceIdSnowDepth = 'snowDepth';
    apiLayerIdCloudCover = 'satellite-global';
    apiLayerIdRadar = 'radar';
    apiLayerIdSnowDepth = 'snowdepth';
    sourceCloudCover: SourceSpecification;
    sourceRadar: SourceSpecification;
    sourceSnowDepth: SourceSpecification;
    layerCloudCover: RasterLayerSpecification;
    layerRadar: RasterLayerSpecification;
    layerSnowDepth: RasterLayerSpecification;

    private consumerId = 'FFLBTPaJzHTDZNsOyTgD6';
    private consumerSecret = 'TwmyGmKJVU2fp3oDd9Uuq0R1DSSYZRWyusqJuO3c';
    private layerDefaults = {
        maxzoom: 16,
        minzoom: 0,
        paint: {
            'raster-opacity': {
                stops: [
                    [0, 1],
                    [16, 0],
                ],
            } as RasterLayerSpecification['paint'],
        },
        type: 'raster' as const,
    };
    private sourceDefaults = {
        tileSize: 256,
        type: 'raster' as const,
    };

    constructor(private http: HttpClient) {
        const cloudCoverUri = this.getWeatherUris(this.apiLayerIdCloudCover);
        const radarUri = this.getWeatherUris(this.apiLayerIdRadar);
        const snowDepthUri = this.getWeatherUris(this.apiLayerIdSnowDepth);
        this.sourceCloudCover = {
            ...this.sourceDefaults,
            tiles: cloudCoverUri,
        };
        this.sourceRadar = {
            ...this.sourceDefaults,
            tiles: radarUri,
        };
        this.sourceSnowDepth = {
            ...this.sourceDefaults,
            tiles: snowDepthUri,
        };

        this.layerCloudCover = {
            ...this.layerDefaults,
            id: this.sourceIdCloudCover,
            source: this.sourceIdCloudCover,
        } as RasterLayerSpecification;
        this.layerRadar = {
            ...this.layerDefaults,
            id: this.sourceIdRadar,
            source: this.sourceIdRadar,
        } as RasterLayerSpecification;
        this.layerSnowDepth = {
            ...this.layerDefaults,
            id: this.sourceIdSnowDepth,
            source: this.sourceIdSnowDepth,
        } as RasterLayerSpecification;
    }

    getWeatherUris(layerId: string): string[] {
        return ['1', '2', '3', '4'].map((subDomainId) => {
            return (
                `https://maps${subDomainId}.aerisapi.com/${this.consumerId}_${this.consumerSecret}/${layerId}` +
                `/{z}/{x}/{y}/current.png`
            );
        });
    }

    getCurrentWeatherUris(layerId: string): Observable<string[]> {
        return this.getLayerTimestamps(layerId).pipe(
            map((result) => {
                const files: AerisFile[] = get(result, 'files', []);
                if (files.length === 0) {
                    return [];
                }

                const file = reduce(
                    tail(files),
                    (testFile, resultFile) => {
                        if (testFile) {
                            return this.getNearestFile(testFile, resultFile);
                        }
                        return resultFile;
                    },
                    head(files)
                );

                if (!file) {
                    return [];
                }

                return ['1', '2', '3', '4'].map((subdomainId) => {
                    return this.buildUri(subdomainId, file.time, layerId);
                });
            })
        );
    }

    getLayerTimestamps(layerId: string): Observable<{ files: AerisFile[] }> {
        const url = `https://maps.aerisapi.com/${this.consumerId}_${this.consumerSecret}/${layerId}.json`;

        return this.http.get<{ files: AerisFile[] }>(url);
    }

    private getNearestFile(file1: AerisFile, file2: AerisFile): AerisFile {
        const now = Math.round(Date.now() * 0.001);
        return Math.abs(now - file2.timestamp) < Math.abs(now - file1.timestamp)
            ? file2
            : file1;
    }

    private buildUri(
        subDomainId: string,
        timestring: string,
        layerId: string
    ): string {
        return (
            `https://maps${subDomainId}.aerisapi.com/${this.consumerId}_${this.consumerSecret}/${layerId}` +
            `/{z}/{x}/{y}/${timestring}_${timestring}.png`
        );
    }
}

interface AerisFile {
    maxTimestamp: number;
    minTimestamp: number;
    runTime: number;
    time: string;
    timestamp: number;
}
